import { useSearchParams } from 'react-router'

/**
 * Source de vérité pour l'onglet manager actif (synchronisé à ?view=).
 *
 * Navigation : on push une nouvelle entrée d'historique à chaque changement
 * d'onglet pour que le bouton Back du navigateur ramène à l'onglet précédent
 * (pattern SPA classique : Notion, Linear, Gmail).
 *
 * Si tu changes d'onglet uniquement pour nettoyer une sous-vue (ex: form ouvert),
 * passe `{ replaceHistory: true }` pour ne pas polluer l'historique.
 */
export function useManagerNavigation() {
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = searchParams.get('view') || 'overview'

  const setActiveTab = (
    tab: string,
    options: { replaceHistory?: boolean; params?: Record<string, string> } = {},
  ) => {
    const next = new URLSearchParams(searchParams)
    if (tab === 'overview') next.delete('view')
    else next.set('view', tab)
    // Nettoie les query params spécifiques aux sous-vues quand on change d'onglet.
    SUBVIEW_PARAMS.forEach((p) => next.delete(p))
    // Sous-vue ciblée (ex. ouvrir directement la fiche d'un agent).
    Object.entries(options.params ?? {}).forEach(([k, v]) => next.set(k, v))
    setSearchParams(next, { replace: options.replaceHistory ?? false })
  }

  return { activeTab, setActiveTab }
}

/** Query params utilisés par les sous-vues de chaque onglet. */
const SUBVIEW_PARAMS = [
  'action',       // 'create-inbound' | 'create-outbound'
  'edit',         // id pour formulaire d'édition
  'history',      // id pour l'historique des appels (AI agent)
  'analytics',    // id pour analytics (AI agent)
  'pdView',       // power dialer : 'create' | 'edit'
  'pdId',         // power dialer : id en édition
  'pdSection',    // power dialer : 'campaigns' | 'numbers'
  'bcView',       // batch calls : 'create' | 'edit'
  'bcId',         // batch calls : id en édition
  'integ',        // integrations : 'keys' | 'webhooks'
  'secret',       // webhooks : id du webhook dont le lien ouvre le secret de signature
  'app',          // marketplace : id intégration sélectionnée (zapier, hubspot, ...)
  'agentTab',     // ai agent form : 'behavior' | 'knowledge' | 'actions' | ...
  'connect',      // logiciels connectés : code d'intégration du lien direct d'un partenaire
  'transfer',     // numéros : la fiche d'un transfert de numéro
]
